import { debugFlag, debugFlagWait } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { setColorMode } from '@/app/theme/colors';
import { prepareBitmapFontInformation } from '@/app/web-workers/renderWebWorker/renderBitmapFonts';
import type {
  ClientRenderInit,
  ClientRenderMessage,
  RenderClientColumnMaxWidth,
  RenderClientMessage,
  RenderClientRowMaxHeight,
} from '@/app/web-workers/renderWebWorker/renderClientMessages';
import {
  compileWasmModule,
  isWasmAssetUnavailableError,
  isWasmUnsupportedBrowserError,
} from '@/app/web-workers/wasmModule';
import { sharedEvents } from '@/shared/sharedEvents';
import { formatWorkerErrorMessage, isBrowserAbortError, isLikelyBrowserAbort } from '@/shared/utils/browserLifecycle';
import type { Rectangle } from 'pixi.js';

function isDarkMode(): boolean {
  return document.documentElement.classList.contains('dark');
}

class RenderWebWorker {
  private worker?: Worker;
  private id = 0;
  private waitingForResponse: Record<number, Function> = {};

  // render may start working before pixiApp is initialized (b/c React is SLOW)
  private preloadQueue: MessageEvent<RenderClientMessage>[] = [];

  initWorker() {
    if (!this.worker) {
      this.worker = new Worker(new URL('./worker/render.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = this.handleMessage;
      this.worker.onerror = (e) => {
        const formatted = formatWorkerErrorMessage(e, 'render.worker error (no details available)');
        const suffix = isLikelyBrowserAbort(e) ? ' (suppressed, browser-induced abort)' : '';
        console.warn(`[render.worker] error${suffix}: ${formatted}`, e);
      };

      // Share the single main-thread-compiled wasm module so the worker
      // instantiates the same compiled code instead of fetching/compiling its
      // own copy.
      compileWasmModule()
        .then((module) => this.send({ type: 'clientRenderInitWasm', module }))
        .catch((e) => {
          // A stale wasm asset after a deploy is an expected client/deploy-race
          // condition that's already recovering via a reload — log at warn so it
          // doesn't pollute the Sentry error feed. If a reload wasn't triggered
          // (persistent failure after we already reloaded once), it's a genuine
          // problem, so keep it at error.
          if (isWasmAssetUnavailableError(e) && e.reloadTriggered) {
            console.warn('[renderWebWorker] wasm asset unavailable, reloading for fresh assets:', e);
          } else if (isWasmUnsupportedBrowserError(e)) {
            // The engine is too old for the wasm we ship. `routes/file.tsx`
            // already shows the "Browser not supported" page and reports it at
            // `info`, so an `error` here is a duplicate of a known, unfixable
            // client condition.
            console.warn('[renderWebWorker] wasm not supported by this browser:', e);
          } else if (isBrowserAbortError(e)) {
            // The page navigated away while the ~27 MB wasm body was still
            // streaming, so the browser cancelled it along with every other
            // in-flight request. Redirect-only routes like /files/create hit
            // this every time: the root loader starts the preload, then the
            // loader redirects out from under it. Nothing failed and nobody is
            // waiting on the module, so don't report it as an error.
            console.warn('[renderWebWorker] wasm compile aborted by navigation:', e);
          } else {
            console.error('[renderWebWorker] Failed to compile wasm module:', e);
          }
        });
    }
  }

  async init(coreMessagePort: MessagePort) {
    if (!this.worker) {
      throw new Error('Expected worker to be initialized in renderWebWorker.init');
    }
    const message: ClientRenderInit = {
      type: 'clientRenderInit',
    };
    this.worker.postMessage(message, [coreMessagePort]);

    // Forward dataTablesCache updates to render worker for table clipping
    events.on('dataTablesCacheRaw', this.updateDataTablesCache);

    // Listen for theme changes and update worker + main thread colors
    sharedEvents.on('changeThemeAppearanceMode', this.updateTheme);

    // Send initial theme state
    this.updateTheme();

    if (await debugFlagWait('debugWebWorkers')) console.log('[renderWebWorker] initialized.');
  }

  private updateTheme = () => {
    const dark = isDarkMode();
    setColorMode(dark);
    this.send({ type: 'clientRenderTheme', isDark: dark });
    // Force viewport dirty to trigger re-render
    pixiApp.setViewportDirty();
  };

  private updateDataTablesCache = (sheetId: string, dataTablesCache: Uint8Array) => {
    this.send({ type: 'clientRenderDataTablesCache', sheetId, dataTablesCache });
  };

  private handleMessage = (e: MessageEvent<RenderClientMessage>) => {
    if (debugFlag('debugWebWorkersMessages')) console.log(`[RenderWebWorker] message: ${e.data.type}`);
    if (!content.cellsSheets) {
      this.preloadQueue.push(e);
      return;
    }

    switch (e.data.type) {
      case 'renderClientCellsTextHashClear':
        content.cellsSheets.cellsTextHashClear(e.data);
        return;

      case 'renderClientLabelMeshEntry':
        content.cellsSheets.labelMeshEntry(e.data);
        return;

      case 'renderClientFinalizeCellsTextHash':
        content.cellsSheets.finalizeCellsTextHash(e.data);
        return;

      case 'renderClientFirstRenderComplete':
        pixiApp.firstRenderComplete();
        return;

      case 'renderClientUnload':
        content.cellsSheets.unload(e.data);
        return;
    }

    if (e.data.id !== undefined) {
      const callback = this.waitingForResponse[e.data.id];
      if (callback) {
        callback(e.data);
        delete this.waitingForResponse[e.data.id];
        return;
      } else {
        console.warn('No callback for id in renderWebWorker', e.data.id);
      }
    }

    console.warn('Unhandled message type', e.data);
  };

  private send(message: ClientRenderMessage) {
    if (!this.worker) {
      throw new Error('Expected worker to be initialized in renderWebWorker.send');
    }

    this.worker.postMessage(message);
  }

  pixiIsReady(sheetId: string, bounds: Rectangle, scale: number, clampBounds?: Rectangle) {
    this.preloadQueue.forEach((message) => this.handleMessage(message));
    this.preloadQueue = [];
    this.updateViewport(sheetId, bounds, scale, clampBounds);
  }

  updateViewport(sheetId: string, bounds: Rectangle, scale: number, clampBounds?: Rectangle) {
    this.send({ type: 'clientRenderViewport', sheetId, bounds, scale, clampBounds });
  }

  updateSheetOffsetsTransient(sheetId: string, column: number | null, row: number | null, delta: number) {
    this.send({
      type: 'clientRenderSheetOffsetsTransient',
      sheetId,
      column,
      row,
      delta,
    });
  }

  showLabel(sheetId: string, x: number, y: number, show: boolean) {
    this.send({
      type: 'clientRenderShowLabel',
      sheetId,
      x,
      y,
      show,
    });
  }

  getCellsColumnMaxWidth(sheetId: string, column: number): Promise<number> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: RenderClientColumnMaxWidth) => resolve(message.maxWidth);
      this.send({
        type: 'clientRenderColumnMaxWidth',
        id,
        sheetId,
        column,
      });
    });
  }

  getCellsRowMaxHeight(sheetId: string, row: number): Promise<number> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: RenderClientRowMaxHeight) => resolve(message.maxHeight);
      this.send({
        type: 'clientRenderRowMaxHeight',
        id,
        sheetId,
        row,
      });
    });
  }

  sendBitmapFonts() {
    this.send({
      type: 'clientRenderBitmapFonts',
      bitmapFonts: prepareBitmapFontInformation(),
    });
  }

  /**
   * Resets the render worker state for loading a new file.
   * Clears pending responses and preload queue without terminating the worker.
   */
  reset() {
    this.waitingForResponse = {};
    this.preloadQueue = [];
    this.send({ type: 'clientRenderReset' });
    events.off('dataTablesCacheRaw', this.updateDataTablesCache);
  }

  /**
   * Terminates the render worker entirely.
   * Used when unloading the application.
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
    this.waitingForResponse = {};
    this.preloadQueue = [];
    events.off('dataTablesCacheRaw', this.updateDataTablesCache);
  }

  /**
   * Returns whether the worker is currently initialized.
   */
  isInitialized(): boolean {
    return !!this.worker;
  }
}

export const renderWebWorker = new RenderWebWorker();
